import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, ArrowRight, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import axios from 'axios';
import {
  sortEventsByDateDesc,
  getCalendarPanelEvents,
  hasEventOnDay,
} from '../utils/eventsSource';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { value: 'all', label: 'All Events', color: 'bg-stone-500' },
  { value: 'walk', label: 'Guided Walks', color: 'bg-countryside-green' },
  { value: 'volunteer', label: 'Volunteer Days', color: 'bg-countryside-terracotta' },
  { value: 'community', label: 'Community Events', color: 'bg-amber-500' },
  { value: 'workshop', label: 'Workshops', color: 'bg-purple-500' },
];

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const twelveMonthsAgo = () => {
  const date = startOfToday();
  date.setFullYear(date.getFullYear() - 1);
  return date;
};

const filterEventsByTab = (events, tab) => {
  const today = startOfToday();
  const cutoff = twelveMonthsAgo();

  if (tab === 'upcoming') {
    return events.filter((event) => new Date(event.event_date) >= today);
  }

  return events.filter((event) => {
    const eventDate = new Date(event.event_date);
    return eventDate < today && eventDate >= cutoff;
  });
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('all');
  const [eventTab, setEventTab] = useState('upcoming'); // 'upcoming' or 'past'
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

  useEffect(() => {
    const fetchEvents = async () => {
      const fallbackEvents = [
          {
            id: '1',
            title: 'Climate Change Webinar',
            description: 'Responding to climate change - an expert discussion with Professor Sir Jim Skea. Interactive Q&A session included.',
            location: 'Online',
            event_date: '2026-04-08T18:30:00Z',
            type: 'workshop',
            image_url: '/images/cpre images for website/24.jpg',
            spots_available: 100,
            speaker: 'Professor Sir Jim Skea',
            featured: true,
          },
          {
            id: '3',
            title: 'AGM & Annual Lunch 2026',
            description: 'Join us for our Annual General Meeting at 10:30am followed by our celebratory Annual Lunch at 12:30pm. Guest speaker: Jackie Copley.',
            location: 'The Woodhouse Arms, Corby Glen',
            event_date: '2026-04-22T10:30:00Z',
            type: 'community',
            image_url: '/images/cpre images for website/25.jpg',
            spots_available: 60,
          },
          {
            id: '4',
            title: 'Planning Surgery Session',
            description: 'Get advice on planning matters and how to respond to applications. One-hour drop-in session.',
            location: 'Online',
            event_date: '2026-04-24T12:00:00Z',
            type: 'workshop',
            image_url: '/images/cpre images for website/28.jpg',
            spots_available: 30,
          },
          {
            id: '5',
            title: 'Future Rural Book Launch',
            description: 'Launch event for the Future Rural publication. Join us for readings, discussion, and refreshments.',
            location: 'Daunt Books, London',
            event_date: '2026-04-29T18:30:00Z',
            type: 'community',
            image_url: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600',
            spots_available: 40,
          },
          {
            id: '6',
            title: 'Centenary Celebration Walk',
            description: 'Join us for a guided countryside walk celebrating 100 years of CPRE.',
            location: 'Rutland Water Visitor Centre',
            event_date: '2026-06-10T10:00:00Z',
            type: 'walk',
            image_url: 'https://images.unsplash.com/photo-1759938894506-965368ace0f8?w=600',
            spots_available: 25,
          },
          {
            id: '7',
            title: 'Volunteer Tree Planting Day',
            description: 'Help plant native trees as part of our Centenary 100 Oaks initiative. Tools and refreshments provided.',
            location: 'Oakham Community Woodland',
            event_date: '2026-05-22T09:30:00Z',
            type: 'volunteer',
            image_url: '/images/cpre images for website/7.jpg',
            spots_available: 40,
          },
          {
            id: '8',
            title: 'Wildflower Meadow Walk',
            description: 'Explore beautiful wildflower meadows with our expert botanist.',
            location: 'South Lincolnshire Reserves',
            event_date: '2026-06-15T10:00:00Z',
            type: 'walk',
            image_url: '/images/cpre images for website/15.jpg',
            spots_available: 20,
          },
      ];

      try {
        const response = await axios.get(`${API}/events`, {
          params: { upcoming_only: false, limit: 50 },
        });
        const data = response.data || [];
        setEvents(
          data.length > 0 ? sortEventsByDateDesc(data) : sortEventsByDateDesc(fallbackEvents)
        );
      } catch (error) {
        console.error('Failed to fetch events:', error);
        setEvents(sortEventsByDateDesc(fallbackEvents));
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-GB', { month: 'short' }),
      weekday: date.toLocaleDateString('en-GB', { weekday: 'short' }),
      time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      full: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
    };
  };

  const tabFilteredEvents = useMemo(
    () => filterEventsByTab(events, eventTab),
    [events, eventTab]
  );

  const filteredEvents = useMemo(
    () =>
      sortEventsByDateDesc(
        tabFilteredEvents.filter((e) => filterType === 'all' || e.type === filterType)
      ),
    [tabFilteredEvents, filterType]
  );

  const calendarPanelEvents = useMemo(
    () => getCalendarPanelEvents(filteredEvents, selectedDate),
    [filteredEvents, selectedDate]
  );

  const getEventTypeColor = (type) => {
    return eventTypes.find(t => t.value === type)?.color || 'bg-stone-500';
  };

  const eventHasDay = useMemo(() => hasEventOnDay(tabFilteredEvents), [tabFilteredEvents]);

  return (
    <Layout>
      <div data-testid="events-page">
        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-countryside-green">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.unsplash.com/photo-1759938894506-965368ace0f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
              alt="Events"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container-custom text-center text-white">
            <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-6 inline-flex items-center gap-2 rounded-full text-sm font-medium px-4 py-2">
              <Calendar className="w-4 h-4" />
              Join Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-white">Events Calendar</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Discover walks, workshops, volunteer days, and community gatherings across Rutland and Lincolnshire.
            </p>
          </div>
        </section>

        {/* Breadcrumbs & Filters */}
        <section className="py-6 border-b border-stone-200 bg-white sticky top-20 z-30">
          <div className="container-custom">
            <Breadcrumbs customItems={[{ label: 'Events' }]} />
            
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between mt-4">
              <div className="flex flex-wrap gap-4 items-center">
              {/* Upcoming / Past Events Tabs */}
              <div className="flex gap-2 bg-stone-100 rounded-lg p-1">
                <Button
                  variant={eventTab === 'upcoming' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setEventTab('upcoming')}
                  className={eventTab === 'upcoming' ? 'bg-cpre-autumn-leaf text-white shadow-sm hover:bg-cpre-autumn-leaf/90' : 'text-countryside-green hover:bg-countryside-green hover:text-white'}
                  data-testid="events-tab-upcoming"
                >
                  Upcoming
                </Button>
                <Button
                  variant={eventTab === 'past' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setEventTab('past')}
                  className={eventTab === 'past' ? 'bg-cpre-autumn-leaf text-white shadow-sm hover:bg-cpre-autumn-leaf/90' : 'text-countryside-green hover:bg-countryside-green hover:text-white'}
                  data-testid="events-tab-past"
                >
                  Past Events
                </Button>
              </div>

              {/* Event Type Filters */}
              <div className="flex flex-wrap gap-2">
                {eventTypes.map((type) => (
                  <Button
                    key={type.value}
                    variant={filterType === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterType(type.value)}
                    className={`rounded-full ${
                      filterType === type.value 
                        ? `${type.color} hover:opacity-90 border-0` 
                        : 'border-stone-300'
                    }`}
                    data-testid={`filter-${type.value}`}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
              </div>

              {/* View Toggle */}
              <div className="flex gap-2 bg-stone-100 rounded-lg p-1">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-cpre-autumn-leaf text-white shadow-sm hover:bg-cpre-autumn-leaf/90' : 'text-countryside-green hover:bg-countryside-green hover:text-white'}
                >
                  List View
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className={viewMode === 'calendar' ? 'bg-cpre-autumn-leaf text-white shadow-sm hover:bg-cpre-autumn-leaf/90' : 'text-countryside-green hover:bg-countryside-green hover:text-white'}
                >
                  Calendar
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Events Content */}
        <section className="section-padding">
          <div className="container-custom">
            {viewMode === 'calendar' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Calendar */}
                <Card className="bg-white border-stone-200 lg:col-span-1">
                  <CardContent className="p-6">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      defaultMonth={selectedDate}
                      modifiers={{
                        hasEvent: eventHasDay,
                      }}
                      modifiersStyles={{
                        hasEvent: { 
                          fontWeight: 'bold',
                          backgroundColor: 'hsl(131, 34%, 27%)',
                          color: 'white',
                          borderRadius: '50%'
                        }
                      }}
                      className="rounded-md"
                    />
                    <div className="mt-4 pt-4 border-t border-stone-100">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-3 h-3 bg-countryside-green rounded-full" />
                        <span>Days with events</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Events for selected date / upcoming */}
                <div className="lg:col-span-2 space-y-6">
                  <h3 className="font-heading font-semibold text-xl">
                    {eventTab === 'upcoming' ? 'Upcoming Events' : 'Past Events'}
                  </h3>
                  {calendarPanelEvents.map((event, index) => {
                    const dateInfo = formatDate(event.event_date);
                    return (
                      <Card 
                        key={event.id}
                        className="bg-white border-stone-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                        data-testid={`calendar-event-${index}`}
                      >
                        <div className="flex">
                          <div className="w-24 flex-shrink-0 bg-countryside-bone flex flex-col items-center justify-center p-4">
                            <span className="text-3xl font-heading font-bold text-countryside-green">{dateInfo.day}</span>
                            <span className="text-sm text-muted-foreground uppercase">{dateInfo.month}</span>
                          </div>
                          <CardContent className="p-4 flex-1">
                            <Badge className={`${getEventTypeColor(event.type)} text-white text-xs mb-2`}>
                              {eventTypes.find(t => t.value === event.type)?.label}
                            </Badge>
                            <h4 className="font-heading font-semibold text-lg mb-1">{event.title}</h4>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {dateInfo.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {event.location}
                              </span>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ) : (
              // List View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {loading ? (
                  [1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="h-96 bg-stone-100 rounded-2xl animate-pulse" />
                  ))
                ) : filteredEvents.length === 0 ? (
                  <div className="col-span-full text-center py-16">
                    <p className="text-muted-foreground text-lg">No events found for this category.</p>
                    <Button onClick={() => setFilterType('all')} className="mt-4" variant="outline">
                      Show All Events
                    </Button>
                  </div>
                ) : (
                  filteredEvents.map((event, index) => {
                    const dateInfo = formatDate(event.event_date);
                    return (
                      <Card 
                        key={event.id}
                        className="bg-white border-stone-200 overflow-hidden group hover:shadow-xl transition-all duration-300"
                        data-testid={`event-card-${index}`}
                      >
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img
                            src={event.image_url || 'https://images.unsplash.com/photo-1759938894506-965368ace0f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600'}
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {/* Date Badge */}
                          <div className="absolute top-4 left-4 bg-white rounded-xl px-4 py-3 text-center shadow-lg">
                            <span className="block text-2xl font-heading font-bold text-countryside-green">{dateInfo.day}</span>
                            <span className="block text-xs font-medium text-muted-foreground uppercase">{dateInfo.month}</span>
                          </div>
                          {/* Type Badge */}
                          <div className="absolute top-4 right-4">
                            <Badge className={`${getEventTypeColor(event.type)} text-white`}>
                              {eventTypes.find(t => t.value === event.type)?.label}
                            </Badge>
                          </div>
                        </div>
                        <CardContent className="p-6">
                          <h3 className="font-heading font-semibold text-xl mb-2 group-hover:text-countryside-green transition-colors duration-200">
                            {event.title}
                          </h3>
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                            {event.description}
                          </p>
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-countryside-green" />
                              <span>{dateInfo.weekday}, {dateInfo.time}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-countryside-green" />
                              <span>{event.location}</span>
                            </div>
                            {event.spots_available && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-countryside-green" />
                                <span>{event.spots_available} spots available</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </section>

        {/* Host Your Own Event */}
        <section className="py-16 bg-countryside-bone/30">
          <div className="container-custom text-center">
            <h2 className="text-2xl font-heading font-bold mb-4">Want to Organize a Community Event?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              We support community groups organizing their own countryside events. Get in touch to learn more.
            </p>
            <Link to="/about#contact">
              <Button className="bg-countryside-green hover:bg-countryside-green-dark text-white rounded-full px-8">
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default EventsPage;
